/**
 * Product Video Gallery Popup
 * Zoom in from video position, zoom out on close. Pauses video on close (button + overlay).
 */
(function () {
  "use strict";

  const CONFIG = {
    zoomInDuration: 400,
    zoomOutDuration: 350,
    easeOut: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    easeIn: "cubic-bezier(0.55, 0.09, 0.68, 0.53)",
    maxViewportRatio: 0.9,
    aspectRatio: 16 / 9,
  };

  const SELECTORS = {
    popup: "#video-gallery-popup",
    player: "#video-gallery-popup-player",
    overlay: ".video-gallery-popup-overlay",
    closeBtn: ".video-gallery-popup-close",
    items: ".section_video_gallery .video-gallery-item",
    dataEl: ".video-gallery-item-data",
    videoEl: ".video_element",
  };

  function getFullSize(aspectRatio) {
    const ratio = aspectRatio && aspectRatio > 0 ? aspectRatio : CONFIG.aspectRatio;
    const maxW = window.innerWidth * CONFIG.maxViewportRatio;
    const maxH = window.innerHeight * CONFIG.maxViewportRatio;
    if (maxW / maxH > ratio) {
      return { w: maxH * ratio, h: maxH };
    }
    return { w: maxW, h: maxW / ratio };
  }

  function pauseMediaInElement(el) {
    if (!el) return;
    const video = el.querySelector("video");
    const iframe = el.querySelector("iframe");
    if (video) {
      try {
        video.pause();
        video.currentTime = 0;
      } catch (_) {}
    }
    if (iframe) {
      try {
        iframe.remove();
      } catch (_) {}
    }
  }

  function clearPlayer(player) {
    if (!player) return;
    try {
      player.innerHTML = "";
    } catch (_) {}
  }

  function setPlayerRect(player, rect, transition = "") {
    player.style.transition = transition;
    player.style.left = rect.left + "px";
    player.style.top = rect.top + "px";
    player.style.width = rect.width + "px";
    player.style.height = rect.height + "px";
    player.style.transform = "none";
  }

  function initYouTubeThumbnailFallbacks() {
    document.querySelectorAll(".video-placeholder-inner[data-fallback]").forEach((el) => {
      const bg = el.style.backgroundImage;
      const url = bg ? bg.replace(/url\(['"]?([^'"]+)['"]?\)/, "$1") : "";
      const fallback = el.getAttribute("data-fallback");
      if (!url || !fallback) return;
      const img = new Image();
      img.onerror = () => {
        el.style.backgroundImage = `url('${fallback}')`;
        el.removeAttribute("data-fallback");
      };
      img.src = url;
    });
  }

  function init() {
    const popup = document.querySelector(SELECTORS.popup);
    const player = document.querySelector(SELECTORS.player);
    if (!popup || !player) return;

    initYouTubeThumbnailFallbacks();

    let sourceRect = null;
    let lastOpenedVideo = null;
    let isClosing = false;

    function close() {
      if (isClosing) return;
      isClosing = true;

      pauseMediaInElement(player);
      if (lastOpenedVideo) {
        pauseMediaInElement(lastOpenedVideo);
        lastOpenedVideo.style.opacity = "1";
        lastOpenedVideo = null;
      }

      if (sourceRect) {
        const rect = { ...sourceRect };
        popup.classList.add("closing");
        const transition = `left ${CONFIG.zoomOutDuration}ms ${CONFIG.easeIn}, top ${CONFIG.zoomOutDuration}ms ${CONFIG.easeIn}, width ${CONFIG.zoomOutDuration}ms ${CONFIG.easeIn}, height ${CONFIG.zoomOutDuration}ms ${CONFIG.easeIn}`;
        setPlayerRect(player, rect, transition);

        setTimeout(() => {
          clearPlayer(player);
          reset();
          isClosing = false;
        }, CONFIG.zoomOutDuration);
      } else {
        clearPlayer(player);
        reset();
        isClosing = false;
      }
      sourceRect = null;
    }

    function reset() {
      popup.classList.remove("active", "closing");
      popup.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
      player.style.transition = "";
      player.style.left = "";
      player.style.top = "";
      player.style.width = "";
      player.style.height = "";
      player.style.transform = "";
    }

    function open(type, url, poster, rect, aspectRatio) {
      sourceRect = { ...rect };
      player.innerHTML = "";
      setPlayerRect(player, rect, "none");

      if (type === "youtube" && url) {
        const sep = url.includes("?") ? "&" : "?";
        const autoplayUrl = url + sep + "autoplay=1&mute=1&playsinline=1";
        const iframe = document.createElement("iframe");
        iframe.setAttribute("allowfullscreen", "");
        iframe.allow = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";
        iframe.style.cssText = "width:100%;height:100%;border:none";
        player.appendChild(iframe);
        popup.classList.add("active");
        popup.setAttribute("aria-hidden", "false");
        document.body.style.overflow = "hidden";
        requestAnimationFrame(() => {
          iframe.src = autoplayUrl;
        });
      } else if (type === "shopify" && url) {
        const video = document.createElement("video");
        video.controls = true;
        video.autoplay = true;
        video.playsInline = true;
        if (poster) video.poster = poster;
        const source = document.createElement("source");
        source.src = url;
        source.type = "video/mp4";
        video.appendChild(source);
        video.style.cssText = "width:100%;height:100%;object-fit:contain";
        player.appendChild(video);
      }

      if (type !== "youtube") {
        popup.classList.add("active");
        popup.setAttribute("aria-hidden", "false");
        document.body.style.overflow = "hidden";
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const { w, h } = getFullSize(aspectRatio);
          const centerLeft = (window.innerWidth - w) / 2;
          const centerTop = (window.innerHeight - h) / 2;
          const transition = `left ${CONFIG.zoomInDuration}ms ${CONFIG.easeOut}, top ${CONFIG.zoomInDuration}ms ${CONFIG.easeOut}, width ${CONFIG.zoomInDuration}ms ${CONFIG.easeOut}, height ${CONFIG.zoomInDuration}ms ${CONFIG.easeOut}`;
          setPlayerRect(player, { left: centerLeft, top: centerTop, width: w, height: h }, transition);
        });
      });
    }

    document.querySelectorAll(SELECTORS.items).forEach((item) => {
      const data = item.querySelector(SELECTORS.dataEl);
      const videoEl = item.querySelector(SELECTORS.videoEl);
      if (!data || !videoEl) return;

      const type = data.getAttribute("data-video-type");
      const url = data.getAttribute("data-video-url");
      const poster = data.getAttribute("data-poster") || "";
      const aspectRatioAttr = data.getAttribute("data-aspect-ratio");
      const aspectRatio = aspectRatioAttr ? parseFloat(aspectRatioAttr) : null;

      item.addEventListener("click", (e) => {
        if (e.target.closest(SELECTORS.closeBtn)) return;
        e.preventDefault();
        pauseMediaInElement(videoEl);
        const rect = videoEl.getBoundingClientRect();
        lastOpenedVideo = videoEl;
        videoEl.style.opacity = "0";
        open(type, url, poster, rect, aspectRatio);
      });
    });

    popup.addEventListener("click", (e) => {
      if (e.target.matches(SELECTORS.overlay) || e.target.closest(SELECTORS.closeBtn)) {
        e.preventDefault();
        e.stopPropagation();
        close();
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && popup.classList.contains("active")) close();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
